/*
 * Stream Console (admin) — per-instance ingestion summary across all
 * tenants. Surfaces the same metrics the workspace's Stream Console
 * shows but pivoted by service profile so platform operators see
 * which connections are noisy / quiet / failing at a glance.
 *
 * Data source: derived from /api/jobs filtered by sync types over the
 * last 24h; counts per environment with status breakdown.
 */

import { useEffect, useMemo, useState } from "react";
import { SkeletonTable } from "../components/Skeleton.js";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { api, ApiError } from "../lib/api.js";

interface JobLike {
  type: string;
  status: string;
  workspaceId?: string;
  input?: { environment?: string };
  startedAt?: string;
  requestedAt: string;
}

const SYNC_TYPES = new Set(["aggregate-metadata-sync", "tracker-metadata-sync", "submitted-aggregate-sync", "tracker-events-sync"]);

export default function StreamConsoleAdmin() {
  const [jobs, setJobs] = useState<JobLike[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.jobs()
      .then((r) => setJobs(r.jobs as unknown as JobLike[]))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, []);

  const summary = useMemo(() => {
    if (!jobs) return null;
    const since = Date.now() - 24 * 60 * 60 * 1000;
    const inWindow = jobs.filter((j) => SYNC_TYPES.has(j.type) && Date.parse(j.startedAt ?? j.requestedAt) >= since);
    type Slot = { total: number; completed: number; failed: number; running: number; lastAt: string | null; perType: Record<string, number> };
    const byEnv = new Map<string, Slot>();
    for (const j of inWindow) {
      const env = j.input?.environment ?? "(unspecified)";
      const slot = byEnv.get(env) ?? { total: 0, completed: 0, failed: 0, running: 0, lastAt: null, perType: {} };
      slot.total += 1;
      if (j.status === "completed") slot.completed += 1;
      else if (j.status === "failed" || j.status === "dead-lettered") slot.failed += 1;
      else if (j.status === "running") slot.running += 1;
      slot.perType[j.type] = (slot.perType[j.type] ?? 0) + 1;
      const at = j.startedAt ?? j.requestedAt;
      if (!slot.lastAt || at > slot.lastAt) slot.lastAt = at;
      byEnv.set(env, slot);
    }
    return [...byEnv.entries()].sort((a, b) => b[1].total - a[1].total);
  }, [jobs]);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Stream console</h1>
          <p className="muted">
            Per-instance ingestion summary over the last 24 hours. Click an environment for the full job timeline.
          </p>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}

      {!summary && <SkeletonTable rows={5} columns={4} />}
      {summary && summary.length === 0 && (
        <div className="empty-state-card">
          <h2 style={{ marginTop: 0 }}>No ingestion in the last 24h</h2>
          <p>Trigger a sync from any console — every sync attempt will appear here aggregated by DHIS2 instance.</p>
        </div>
      )}
      {summary && summary.length > 0 && (
        <table className="transfer-table">
          <thead>
            <tr>
              <th>Environment</th>
              <th style={{ textAlign: "right" }}>Total</th>
              <th style={{ textAlign: "right" }}>Completed</th>
              <th style={{ textAlign: "right" }}>Failed</th>
              <th style={{ textAlign: "right" }}>Running</th>
              <th>Last activity</th>
              <th>Type breakdown</th>
            </tr>
          </thead>
          <tbody>
            {summary.map(([env, slot]) => (
              <tr key={env}>
                <td className="mono" style={{ fontSize: 12 }}>{env}</td>
                <td style={{ textAlign: "right" }}>{slot.total}</td>
                <td style={{ textAlign: "right" }}>{slot.completed}</td>
                <td style={{ textAlign: "right" }}>{slot.failed > 0 ? <span className="pill pill-err">{slot.failed}</span> : 0}</td>
                <td style={{ textAlign: "right" }}>{slot.running}</td>
                <td className="muted" style={{ fontSize: 12 }}>{slot.lastAt ?? "—"}</td>
                <td style={{ fontSize: 12 }}>
                  {Object.entries(slot.perType).map(([t, n]) => <span key={t} className="pill pill-muted" style={{ marginRight: 4 }}>{t}: {n}</span>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
